import {AxiosResponse} from "axios"
import {
    Address,
    Building,
    BuildingFilterOptions,
    BuildingListItem,
    NewBuilding,
    NewOrganizationAddress,
    NewPersonAddress,
    UpdateAddress,
    UpdateBuilding
} from "@/services/models/Address";
import {PagedData} from "@/services/models/shared/PagedData";
import BimsHttpService from "@/services/BimsHttpService";

class AddressService {

    /**
     * @description Retrieves all buildings
     */
    getAllBuildings(): Promise<BuildingListItem[]> {
        return BimsHttpService.get(`/buildings`, {
            params: {
                pageSize: 1
            }
        })
            .then((response: AxiosResponse<PagedData<BuildingListItem>>) => {
                return BimsHttpService.get(`/buildings`, {
                    params: {
                        onlyShowActive: true,
                        pageSize: response.data.totalCount,
                        page: 1
                    }
                })
                    .then((responses: AxiosResponse<PagedData<BuildingListItem>>) => {
                        return responses.data.data;
                    })
            })
    }

    /**
     * @description Retrieves all addresses for the organization with the specified id
     */
    getOrganizationAddresses(organizationId: string): Promise<Address[]> {
        return BimsHttpService.get(`/organizations/${organizationId}/addresses`)
            .then((response: AxiosResponse<Address[]>) => {
                    return response.data;
                }
            )
    }

    /**
     * @description Retrieves all addresses for the person with the specified id
     */
    getPersonAddresses(personId: string): Promise<Address[]> {
        return BimsHttpService.get(`/people/${personId}/addresses`)
            .then((response: AxiosResponse<Address[]>) => {
                    return response.data;
                }
            )
    }

    /**
     * @description Updates the organization address with the specified id
     */
    updateOrganizationAddress(address: UpdateAddress): Promise<any> {
        return BimsHttpService.put(`/organizations/addresses/${address.id}`, address)
    }

    /**
     * @description Updates the person address with the specified id
     */
    updatePersonAddress(address: UpdateAddress): Promise<any> {
        return BimsHttpService.put(`/people/addresses/${address.id}`, address)
    }

    /**
     * @description Creates a new address of the given type for the specified organization
     */
    createOrganizationAddress(address: NewOrganizationAddress): Promise<any> {
        return BimsHttpService.post(`/organizations/${address.organizationId}/addresses`, address)
    }

    /**
     * @description Creates a new address of the given type for the specified person
     */
    createPersonAddress(address: NewPersonAddress): Promise<any> {
        return BimsHttpService.post(`/people/${address.personId}/addresses`, address)
    }

    /**
     * @description Returns a paged list of buildings.
     */
    getBuildingList(filter: BuildingFilterOptions
    ): Promise<PagedData<BuildingListItem>> {
        return BimsHttpService.get(`/buildings`, {
            params: filter
        }).then((response: AxiosResponse<PagedData<BuildingListItem>>) => {
            return response.data as PagedData<BuildingListItem>;
        });
    }

    /**
     * @description Returns the building with the specified id
     */
    getBuilding(id: string): Promise<Building> {
        return BimsHttpService.get(`/buildings/${id}`).then((response: AxiosResponse<Building>) => {
            return response.data as Building;
        });
    }

    /**
     *  @description Adds a new building
     */
    addBuilding(building: NewBuilding): Promise<string> {
        return BimsHttpService.post(`/buildings`,
            building
        ).then(response => {
            return response.data
        });
    }

    /**
     * @description Updates the building
     */
    updateBuilding(building: UpdateBuilding): Promise<any> {
        return BimsHttpService.put(`/buildings/${building.id}`,
            building
        )
    }

    /**
     * @description Deletes the building
     */
    deleteBuilding(id: string): Promise<any> {
        return BimsHttpService.delete(`/buildings/${id}`)
    }
}

export default new AddressService();
